/**
 * The machine-local print nudge, for the HTML print path (F-FE-N156).
 *
 * F-FE-N113 added "push slips right by N" for the phone-printed KOTs: N literal
 * spaces in front of every ESC/POS line, stored per device in localStorage
 * (`stationPrinterMap.ts`). KOF then hit the same symptom — the first few
 * characters cut off at the left — on the DESKTOP app, which prints HTML through
 * printToPDF + SumatraPDF and never read the number at all.
 *
 * This module is the HTML-side twin of that inset. Same stored value, same unit,
 * applied as CSS instead of spaces, at the one seam every desktop slip passes
 * through (`printTransport.ts`), so receipts, KOTs, Z-reports, credit notes,
 * tax invoices, POs and the counter relay all move together.
 *
 * ── The unit ──
 * One step is one ESC/POS column: 12 dots wide (`CHAR_DOTS`) on a 203-dpi head,
 * which is 8 dots per mm, so a column is 1.5 mm — on a 58 mm roll (384 dots,
 * 32 columns) exactly as on an 80 mm one (576 dots, 48 columns). The number the
 * operator types therefore means the same physical distance on a phone-printed
 * kitchen slip and a desktop-printed receipt.
 *
 * ── Why `left`, not `margin-left` ──
 * The desktop shell force-flushes the slip block left after load
 * (`electron/src/print.ts`: `body > * { margin-left: 0 !important }`), because
 * SumatraPDF anchors the PDF top-left and any centring slop became a rightward
 * shift. That rule is inserted AFTER ours and is also `!important`, so a
 * margin would simply be overwritten. A relative `left` is not touched by it.
 *
 * ── Why the width shrinks ──
 * The ESC/POS path does not push text off the right edge; it lays the slip out
 * on `columns - offset` cells. `max-width: calc(100% - Nmm)` is the same rule
 * here: the body is the paper width, so a 72 mm receipt on an 80 mm roll only
 * narrows once the nudge would actually carry it past the roll.
 */

/** Millimetres per offset column: 12 dots per character at 8 dots per mm. */
export const MM_PER_OFFSET_COL = 1.5

/** Mirrors `MAX_OFFSET_COLS` in stationPrinterMap.ts — wider loses more than it gains. */
const MAX_COLS = 8

function clampCols(cols: number): number {
  const n = Math.round(Number(cols) || 0)
  if (!Number.isFinite(n) || n <= 0) return 0
  return Math.min(n, MAX_COLS)
}

/** The nudge in millimetres for a stored column count. */
export function printOffsetMm(cols: number): number {
  return clampCols(cols) * MM_PER_OFFSET_COL
}

/** The CSS that moves every top-level slip block right by the nudge. Empty for 0. */
export function printOffsetCss(cols: number): string {
  const mm = printOffsetMm(cols)
  if (mm <= 0) return ''
  return `body > * { position: relative !important; left: ${mm}mm !important; max-width: calc(100% - ${mm}mm) !important; }`
}

/**
 * The slip document with the nudge applied. Returns the SAME string when the
 * nudge is 0, so a machine that never set one prints byte-identical HTML to
 * what it printed before this existed.
 */
export function withPrintOffset(html: string, cols: number): string {
  const css = printOffsetCss(cols)
  if (!css) return html
  const tag = `<style data-print-offset="${clampCols(cols)}">${css}</style>`
  const i = html.indexOf('</head>')
  if (i >= 0) return html.slice(0, i) + tag + html.slice(i)
  // No head to hang it on — put it first, where it still applies to the body.
  return tag + html
}
